import { useMemo } from 'react';
import type { ChildInfo } from '@/components/Layout/mobile/ChildFilterPills';
import type { TaskRow, EventRow, ChildRef } from '@/lib/schedule';
import { getChildrenFromTask, getChildrenFromEvent } from '@/lib/schedule';

/**
 * Keep the avatar fields the filter pills need. The Today/Schedule selects
 * already fetch emoji_color + avatar_image_path alongside the emoji
 * (schedule.service TASK_SELECT/EVENT_SELECT); dropping them here is what
 * left the pills stuck on the emoji after a photo upload (YAR-363).
 */
function toChildInfo(c: ChildRef): ChildInfo {
  return {
    id: c.id,
    name: c.name,
    emoji: c.emoji_avatar ?? undefined,
    color: c.emoji_color ?? undefined,
    imagePath: c.avatar_image_path ?? undefined,
  };
}

/**
 * Distinct children across tasks + events. Reads from the multi-child
 * join (task_children / event_children) populated via the Supabase
 * select(), and falls back to the row's scalar child_id if the join
 * isn't present for that record.
 */
export function useChildList(
  tasks: TaskRow[],
  events: EventRow[],
): ChildInfo[] {
  return useMemo(() => {
    const map = new Map<string, ChildInfo>();
    for (const task of tasks) {
      for (const c of getChildrenFromTask(task)) {
        if (!map.has(c.id)) {
          map.set(c.id, toChildInfo(c));
        }
      }
    }
    for (const event of events) {
      for (const c of getChildrenFromEvent(event)) {
        if (!map.has(c.id)) {
          map.set(c.id, toChildInfo(c));
        }
      }
    }
    return Array.from(map.values());
  }, [tasks, events]);
}
